import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { LiveStatus } from '@/lib/live'
import { cn } from '@/lib/utils'

const LABEL: Record<LiveStatus, string> = {
  live: 'Live',
  connecting: 'Connecting',
  offline: 'Offline',
}

const HINT: Record<LiveStatus, string> = {
  live: 'Changes appear here as they happen.',
  connecting: 'Connecting to live updates…',
  offline: 'Live updates paused. Reconnecting automatically.',
}

/** Visible live-update status. `data-state` lets browser checks read it. */
export function LiveIndicator({ status }: { status: LiveStatus }) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            role="status"
            aria-live="polite"
            data-testid="live-status"
            data-state={status}
            className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs text-muted-foreground max-sm:size-7 max-sm:justify-center max-sm:px-0"
          />
        }
      >
        <span
          aria-hidden="true"
          className={cn(
            'size-2 rounded-full',
            status === 'live' && 'bg-emerald-500',
            status === 'connecting' && 'animate-pulse bg-amber-500',
            status === 'offline' && 'bg-muted-foreground/50',
          )}
        />
        {/* Phones: the dot only; the word stays for screen readers. */}
        <span className="max-sm:sr-only">{LABEL[status]}</span>
      </TooltipTrigger>
      <TooltipContent>{HINT[status]}</TooltipContent>
    </Tooltip>
  )
}
